'use client';

import CommonBreadcrumbs from '@/components/CommonBreadcrumbs';
import { GET_CATEGORY_DETAIL, GET_CATEGORY_FILTER_DATA } from '@/graphql/queries';
import { cn } from '@/lib/utils';
import { buildCategoryTree } from '@/util/build-category-tree';
import { gql, useQuery } from '@apollo/client';
import clsx from 'clsx';
import { Fragment, ReactNode, Suspense, useMemo } from 'react';
import FilterModalOpenButton from '../FilterModalOpenButton';
import FilterSideBarLazyLoad from '../FilterSidebar/filter-side-bar-lazy-load';
import ProductSort from '../ProductSort';
import { AspectRatio } from '../ui/aspectRatio';
import { CategoryBanner } from './category-banner';
import { CategoryDescriptionElement } from './category-description-element';
import { Content } from './content';
import { ContentPagination } from './content-pagination';
import { useGetCategoryDetail } from './use-get-category-detail';

import dynamic from 'next/dynamic';

const CategoryNoIndex = dynamic(() => import('./category-no-index'), { ssr: false });

export const CategoryPage = ({
  aggregations,
  sortState,
  pageSize,
  categorySortOptions,
  categoryFilterOptions,
  categoryFilterCategoryOptions,
  categoryDetail,
  filterState,
  categoryFaqNode,
  useServerState,
  categoryDetailVariables,
  filters,
  categoryName,
  categoryUrlKey,
}: {
  uid: string;
  pageSize: number;
  filters: any;
  aggregations: any;
  sortState: {
    sortId: string;
    sortText: string;
    sortAttribute: string;
    sortDirection: string;
    sortQuery: string;
  };
  filterState: FilterState;
  categoryDetail: any;
  categorySortOptions: any;
  categoryFilterOptions: any[];
  categoryFilterCategoryOptions: any[];
  categoryFaqNode: ReactNode;
  useServerState: boolean;
  categoryDetailVariables: any;
  categoryUrlKey: string;
  categoryName: string;
}) => {
  const optionIdMapToRewrite = useMemo(() => {
    return aggregations.reduce((acc, cur) => {
      const { option_id, rewrite, code } = cur;

      if (!rewrite || !option_id || !code) {
        return acc;
      }

      if (acc[code]) {
        acc[code][option_id] = rewrite;
      } else {
        acc[code] = {
          [option_id]: rewrite,
        };
      }

      return acc;
    }, {});
  }, [aggregations]);
  const categoryTree = useMemo(() => {
    return buildCategoryTree(categoryFilterCategoryOptions.map(x => ({ ...x })) || []);
  }, [categoryFilterCategoryOptions]);

  const { data: clientCategoryFilterRes, loading: clientCategoryFilterLoading } = useQuery(
    gql(GET_CATEGORY_FILTER_DATA),
    {
      skip: useServerState,
      variables: { filter: filters },
      fetchPolicy: 'cache-and-network',
    },
  );
  const { data: clientCategoryDetailRes, loading: clientCategoryDetailLoading } = useQuery(gql(GET_CATEGORY_DETAIL), {
    skip: useServerState,
    variables: categoryDetailVariables,
    fetchPolicy: 'cache-and-network',
  });

  const clientCategoryFilterOptions = useMemo(() => {
    return useServerState
      ? []
      : clientCategoryFilterRes?.products?.aggregations?.filter(
          x => !!x?.attribute_code && x.attribute_code !== 'colors-family',
        ) || [];
  }, [useServerState, clientCategoryFilterRes]);

  const { categoriesData, categoryShortDes, noProducts } = useGetCategoryDetail({
    categoryDetail: useServerState ? categoryDetail : clientCategoryDetailRes,
  });
  const pageInfo = categoriesData?.products?.page_info;
  const totalCount = categoriesData?.products?.total_count ?? 0;

  return (
    <Fragment>
      <CategoryNoIndex filterState={filterState} />
      <article
        className={cn('mx-auto max-w-[1720px]', 'pb-[40px] lg_pb-[100px]')}
        data-total-pages={pageInfo?.total_pages || 1}
      >
        <CategoryBanner categoryData={categoriesData} />
        <div className='lg_py-[20px]'>
          <CommonBreadcrumbs
            paths={[
              {
                text: 'Home',
                path: '/',
              },
              {
                text: categoryName,
                current: true,
              },
            ]}
          />
        </div>
        <div className='mx-[15px] mt-[15px] border-b border-[#ddd] pb-[30px] lg_mx-0'>
          <div className='mb-[6px] font-poppins text-[24px] font-[600] leading-[1.4] text-title'>
            <h1 className='inline-block'>{categoryName}</h1>
            <span className='ml-2 font-sans text-[16px] text-[#666]'>({totalCount} Items)</span>
          </div>
          {categoryShortDes && (
            <div
              dangerouslySetInnerHTML={{
                __html: categoryShortDes,
              }}
              className='mt-[10px] w-[800px] max-w-full font-sans text-[16px] leading-[1.4] text-title lg_text-[18px]'
            ></div>
          )}
        </div>
        <h2 className='hidden'>Browse All {categoryName}</h2>

        <div className='mx-[15px] mb-[30px] flex justify-between border-b border-[#ddd] lg_mx-0'>
          <div className='hidden h-[60px]  items-center font-poppins text-[20px] font-[600] leading-[1.4] text-title lg_flex'>
            Filters
          </div>
          <FilterModalOpenButton
            filterOptions={useServerState ? categoryFilterOptions : clientCategoryFilterOptions}
            filterState={filterState}
            categoryUrlKey={categoryUrlKey}
            categoryTree={categoryTree}
            isSearchPage={false}
            optionIdMapToRewrite={optionIdMapToRewrite}
            className={clsx(clientCategoryFilterLoading ? 'invisible' : '')}
          />
          <div className='h-8 w-[1px] self-center bg-[#ddd] lg_hidden'></div>
          <Suspense fallback={<div className='h-[60px] basis-1/2 lg_basis-auto'></div>}>
            <ProductSort
              sortState={sortState}
              availableSortMethods={categorySortOptions}
              isSearchPage={false}
            />
          </Suspense>
        </div>
        <div className='flex w-full'>
          <div className='hidden border-r border-[#ddd] pr-[46px] lg_mr-[46px] lg_flex lg_w-[304px] lg_self-start'>
            {clientCategoryFilterLoading ? null : (
              <FilterSideBarLazyLoad
                filterOptions={useServerState ? categoryFilterOptions : clientCategoryFilterOptions}
                filterState={filterState}
                categoryUrlKey={categoryUrlKey}
                categoryTree={categoryTree}
                isSearchPage={false}
                optionIdMapToRewrite={optionIdMapToRewrite}
              />
            )}
          </div>
          <div className='flex-1'>
            {clientCategoryDetailLoading ? (
              <section
                className={clsx(
                  'grid grid-cols-2 gap-x-[15px] gap-y-[40px] px-[15px] lg_grid-cols-4 lg_gap-x-[30px] lg_gap-y-[60px] lg_px-0',
                )}
              >
                {Array.from({ length: 12 }, (_, i) => {
                  return (
                    <div key={i}>
                      <AspectRatio ratio={1}>
                        <div className='h-full animate-pulse bg-[#e2e8f0]'></div>
                      </AspectRatio>
                      <div className='mt-3 h-5 animate-pulse bg-[#e2e8f0]' />
                      <div className='mt-3 h-5 w-[40%] animate-pulse bg-[#e2e8f0]' />
                    </div>
                  );
                })}
              </section>
            ) : (
              <Content
                items={
                  categoriesData?.products?.items ||
                  Array.from({
                    length: pageSize,
                  }).fill(null)
                }
                ads={categoriesData?.categories?.items?.[0]?.adv}
                noProducts={noProducts}
              />
            )}
            <Suspense>
              <ContentPagination pageInfo={pageInfo} />
            </Suspense>
            <CategoryDescriptionElement
              categoriesData={categoriesData}
              categoryName={categoryName ?? ''}
            />
            {categoryFaqNode}
          </div>
        </div>
      </article>
    </Fragment>
  );
};
